import React, { useState, useEffect, useRef } from "react";
import "../../components/ad.css";
import {
  useInitialdataGetQuery,
  useCounsellorGiveMutation,
  useVerifyCounsellorPanMutation,
} from "../../slice/authApiSlice";
import { generatePublicUrl } from "../../config/image";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setLoading } from "../../slice/authSlice";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const getTodayDate = () => new Date().toISOString().split("T")[0];

export default function Counsellors() {
  const { data, isLoading } = useInitialdataGetQuery();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [displayList, setDisplayList] = useState([]);
  const [coin, setCoin] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(getTodayDate());
  const [dateError, setDateError] = useState("");
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const isDownloading = useRef(false);
  const hasLoadedOnce = useRef(false);
  if (data) hasLoadedOnce.current = true;

  const [counsellorGive] = useCounsellorGiveMutation();
  const [verifyCounsellorPan] = useVerifyCounsellorPanMutation();

  useEffect(() => {
    if (!data?.counsellors) return;
    const term = searchTerm.toLowerCase().trim();
    if (!term) {
      setDisplayList(data.counsellors);
      return;
    }
    const filtered = data.counsellors.filter((c) => {
      const name = c.name?.toLowerCase() || "";
      const email = c.email?.toLowerCase() || "";
      const phone = c.phNumber?.toLowerCase() || "";
      return (
        name.includes(term) || email.includes(term) || phone.includes(term)
      );
    });
    setDisplayList(filtered);
  }, [searchTerm, data]);

  useEffect(() => {
    if (!hasLoadedOnce.current) {
      dispatch(setLoading(isLoading));
    } else {
      dispatch(setLoading(false));
    }
  }, [isLoading, data, dispatch]);

  const handleInputChange = (e) => setSearchTerm(e.target.value);
  const handleChangeCoin = (userId, value) =>
    setCoin((prev) => ({ ...prev, [userId]: value }));

  const handleSubmitCounsellorCoin = async (userId) => {
    try {
      await counsellorGive({ userId, coin: parseInt(coin[userId]) }).unwrap();
      toast.success("Coins transferred");
    } catch {
      toast.error("Error");
    }
  };

  const handleVerify = async (id) => {
    try {
      await verifyCounsellorPan(id).unwrap();
      toast.success("Verified");
    } catch {
      toast.error("Error");
    }
  };

  const handleDownloadClick = () => {
    setEndDate(getTodayDate());
    setDateError("");
    setIsModalOpen(true);
  };

  const handleEndDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = getTodayDate();
    if (selectedDate > today) {
      setEndDate(today);
      setDateError("To Date cannot be later than today's date.");
    } else if (startDate && selectedDate < startDate) {
      setEndDate(startDate);
      setDateError("To Date cannot be earlier than the From Date.");
    } else {
      setEndDate(selectedDate);
      setDateError("");
    }
  };

  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    setStartDate(selectedDate);
    if (endDate && selectedDate > endDate) {
      setEndDate(selectedDate);
      setDateError("Adjusted To Date to match your new Start Date.");
    } else {
      setDateError("");
    }
  };

  const handleProcessDownload = async () => {
    if (isDownloading.current) return;
    isDownloading.current = true;

    if (!startDate) {
      toast.error("Please select a starting date");
      isDownloading.current = false;
      return;
    }

    setIsExcelLoading(true);
    let success = false;

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const filteredData = data.counsellors.filter((item) => {
        const createdDate = new Date(item.createdAt);
        return createdDate >= start && createdDate <= end;
      });

      if (filteredData.length === 0) {
        toast.info("No records found.");
        setIsExcelLoading(false);
        isDownloading.current = false;
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Counsellors Report");
      const centerStyle = { vertical: "middle", horizontal: "center" };

      worksheet.columns = [
        { header: "Profile Pic", key: "img", width: 25 },
        { header: "ID", key: "_id", width: 25 },
        { header: "Created At", key: "createdAt", width: 20 },
        { header: "Name", key: "name", width: 20 },
        { header: "Email", key: "email", width: 30 },
        { header: "Phone", key: "phNumber", width: 15 },
        { header: "Role", key: "role", width: 15 },
        { header: "Status", key: "status", width: 12 },
        { header: "Admin Verified", key: "adminVerified", width: 15 },
        { header: "Coins", key: "coins", width: 10 },
        { header: "Chat Price", key: "chatPrice", width: 12 },
        { header: "Voice Price", key: "voiceCallPrice", width: 12 },
        { header: "Session Price", key: "sessionPrice", width: 12 },
        { header: "Rating", key: "rating", width: 10 },
        { header: "Experience", key: "yearExp", width: 12 },
        { header: "Profile Comp %", key: "profileCompletion", width: 15 },
      ];

      worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "007EB9" },
      };
      worksheet.getRow(1).alignment = centerStyle;

      for (let i = 0; i < filteredData.length; i++) {
        const item = filteredData[i];
        const rowIndex = i + 2;

        const row = worksheet.addRow({
          _id: item._id,
          createdAt: new Date(item.createdAt).toLocaleDateString(),
          name: item.name || "N/A",
          email: item.email,
          phNumber: item.phNumber || "N/A",
          role: item.role,
          status: item.status,
          adminVerified: item.adminVerified ? "Yes" : "No",
          coins: item.coins,
          chatPrice: item.chatPrice,
          voiceCallPrice: item.voiceCallPrice,
          sessionPrice: item.sessionPrice,
          rating: item.rating,
          yearExp: item.yearExp,
          profileCompletion: item.profileCompletion,
        });

        row.height = 65;
        row.eachCell((cell) => {
          cell.alignment = centerStyle;
        });

        const profilePicPath = item.profilePictures?.[0]?.img || item.profilePictures?.[0];
        // console.log("profilePictures:", item.profilePictures);
        console.log("Row:", rowIndex);
        console.log("profilePicPath:", profilePicPath);

        if (!profilePicPath) {
          console.warn("No image path for row", rowIndex);
          continue;
        }

          try {
            const imageUrl = generatePublicUrl(profilePicPath);
            console.log("Generated URL: ", imageUrl);
            
            const response = await fetch(imageUrl);
            console.log("Fetch Response:", response); 

            if (!response.ok) throw new Error("Image fetch failed");

            const blob = await response.blob();
            // console.log(blob.type);

            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              // reader.onload = () => resolve(reader.result.split(",")[1]);
              reader.onloadend = () => {
                const result = reader.result;
                if (!result) return reject("Base64 conversion failed");
                resolve(result.toString().split(",")[1]);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

            

            // const extension = blob.type.split("/")[1];

            const imageId = workbook.addImage({
              base64,
              extension: "jpeg",
            });

            worksheet.addImage(imageId, {
              tl: { col: 0.2, row: rowIndex - 0.8 },
              ext: { width: 60, height: 60 },
            });
          } catch (e) {
            console.error("Image error:", e);
            console.warn("Skipping image for row", rowIndex);
          }
        }
      

      const buffer = await workbook.xlsx.writeBuffer();
      const fileBlob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(fileBlob, `Counsellors_Report_${startDate}.xlsx`);
      success = true;
    } catch (error) {
      console.error("Download Error:", error);
      toast.error("Failed to generate file");
    }

    await new Promise((resolve) => setTimeout(resolve, 0));

    setIsExcelLoading(false);
    if (success) {
      toast.success("Excel with images downloaded!");
      setIsModalOpen(false);
    }

    isDownloading.current = false;
  };

  return (
    <div className="mainContentArea">
      <div className="counsellorPage">
        {hasLoadedOnce.current && (
          <div className="counsellorContainer">
            <div className="counsellorHeaderSection">
              <h2>Counsellors Management</h2>
              <div className="headerActions">
                <div className="searchBox">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleInputChange}
                    className="headerSearchInput"
                  />
                </div>
                <button
                  className="downloadBtn"
                  onClick={handleDownloadClick}
                  disabled={isExcelLoading}
                >
                  Download
                </button>
              </div>
            </div>

            <div className="tableWrapper">
              <table className="counsellorTable">
                <thead>
                  <tr>
                    <th>ID Proof</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Coins</th>
                    <th>Add Coins</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {displayList.length > 0 ? (
                    displayList.map((college) => (
                      <tr key={college._id}>
                        <td>
                          <img
                            className="tableThumb"
                            src={generatePublicUrl(college?.legalId?.[0]?.img)}
                            alt="ID"
                          />
                        </td>
                        <td className="boldText">{college?.name}</td>
                        <td>{college?.email}</td>
                        <td>{college?.phNumber || "N/A"}</td>
                        <td>{college?.coins}</td>
                        <td>
                          <div className="tableActionCell">
                            <input
                              value={coin[college._id] || ""}
                              onChange={(e) =>
                                handleChangeCoin(college._id, e.target.value)
                              }
                              className="tableInput"
                              placeholder="0"
                              type="number"
                            />
                            <button
                              onClick={() =>
                                handleSubmitCounsellorCoin(college._id)
                              }
                              className="tableActionBtn"
                            >
                              Give
                            </button>
                          </div>
                        </td>
                        <td>
                          {college?.adminVerified ? (
                            <span className="statusVerified">Verified</span>
                          ) : (
                            <button
                              onClick={() => handleVerify(college._id)}
                              className="tableVerifyBtn"
                            >
                              Verify Now
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="noDataCell">
                        No results found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {isModalOpen && (
              <div className="modalOverlay">
                <div className="datePickerModal">
                  <h3>Download Report</h3>
                  <div className="modalInputs">
                    <div className="inputGroup">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={startDate}
                        max={getTodayDate()}
                        onChange={handleStartDateChange}
                      />
                    </div>
                    <div className="inputGroup">
                      <label>To Date</label>
                      <input
                        type="date"
                        value={endDate}
                        max={getTodayDate()}
                        onChange={handleEndDateChange}
                      />
                      {dateError && (
                        <span className="dateErrorMessage">{dateError}</span>
                      )}
                    </div>
                  </div>
                  <div className="modalActions">
                    <button
                      className="cancelBtn"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="confirmBtn"
                      onClick={handleProcessDownload}
                      disabled={isExcelLoading}
                    >
                      {isExcelLoading ? "Downloading..." : "Download Excel"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
